import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "./DashboardLayout";
import { useUser } from "./Context/UserContext";

const noSpinnerStyle = `
    input.no-spinner::-webkit-outer-spin-button,
    input.no-spinner::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input.no-spinner[type=number] {
        -moz-appearance: textfield;
    }
    .goods-scroll thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: #18212b;
    }
`;

const MODE_OF_PAYMENT = { "1": "Cash", "2": "Bank Transfer", "3": "UPI" };
const PAYMENT_STATUS = { "1": "Non-Billable", "2": "Billable", "3": "Rejected" };


// --- REQUEST approval cascade: DM -> Finance ---
const reqStatusFor = (row) => {
    if (Number(row.isFinanceApproved) === 1) {
        return "FINANCE APPROVED";
    }

    if (Number(row.isDMApproved) === 1) {
        return "DM APPROVED";
    }

    return "PENDING FOR DM APPROVAL";
};

const billStatusFor = (row) => {
    if (Number(row.isBillSent) !== 1) return "NOT SENT";
    if (Number(row.isBillFinanceApproved) === 1) return "FINANCE APPROVED";
    return "PENDING FOR FINANCE APPROVAL";
};


const reqIdSuffixFor = (row) =>
    Number(row.isBillFinanceApproved) === 1
        ? " (ACTIVE)"
        : " (PENDING)";


const statusColor = (text) => {
    const t = (text || "").toUpperCase();
    if (t.includes("APPROVED")) return "#198754";
    if (t.includes("PENDING")) return "#b8860b";
    if (t.includes("REJECT")) return "#dc3545";
    return "#6c757d";
};

// Best-effort display name for the logged-in user — adjust the field
// priority here if your UserContext uses a different shape.
const getCurrentUserName = (user) =>
    user?.name || user?.contactPersonName || user?.NameOfCompany || user?.empId || "";

const PettyCashCredit = () => {
    const { user } = useUser();
    console.log("USER OBJECT:", user);
    const currentUserName = getCurrentUserName(user);
    const currentUserEmpId = user?.empId || "";

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const buildInitialForm = () => ({
        id: null,
        requestType: "Credit",
        employeeId: currentUserEmpId,
        projectDepartmentName: "",
        deliveryManager: "",
        modeOfPayment: "",
        date: getTodayDate(),
        requestedBy: currentUserName,
        paymentStatus: "",
        vendor: "",
        amount: "",
        creditCardNumber: "",
        invoiceType: "",  
        invoiceNumber: ""
    });

    const [form, setForm] = useState(buildInitialForm);
    const [errors, setErrors] = useState({});
    const [requests, setRequests] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [viewMode, setViewMode] = useState("request"); // "request" | "billDetails"
    const emptyBillRow = () => ({
        natureOfExpense: "",
        amount: "",
        igst: "",
        cgst: "",
        sgst: "",
        total: 0,
        documents: [],
        hasSavedDoc: false 
    });

    const [billRows, setBillRows] = useState(
        Array.from({ length: 1 }, emptyBillRow)
    );

    const [billErrors, setBillErrors] = useState({});
    const [savingBill, setSavingBill] = useState(false);
    const [savedDocs, setSavedDocs] = useState([]);
    const [creditCards, setCreditCards] = useState([]);
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [isInvoiceLocked, setIsInvoiceLocked] = useState(false);
    const [isDark, setIsDark] = useState(
        () => document.body.classList.contains("dark-mode")
    );

    useEffect(() => {
        const handler = (e) => setIsDark(!!e.detail);
        window.addEventListener("vms:darkmode", handler);
        return () => window.removeEventListener("vms:darkmode", handler);
    }, []);
    const fetchSavedDocuments = useCallback(async (requestId) => {
        if (!requestId) return;

        try {
            const res = await fetch(`/api/PettyCashCredit/${requestId}/documents`);
            const data = await res.json();

            setSavedDocs(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Fetch Documents Error:", error);
            setSavedDocs([]);
        }
    }, []);

    // ADD THIS BACK — must come after fetchSavedDocuments, not before
    const checkExistingInvoice = useCallback(async (invoiceNo) => {
        if (!invoiceNo || !invoiceNo.trim()) {
            setIsInvoiceLocked(false);
            return;
        }
        try {
            const res = await fetch(`/api/PettyCashCredit/by-invoice/${encodeURIComponent(invoiceNo)}`);
            if (res.status === 404) {
                setIsInvoiceLocked(false);
                return;
            }
            const data = await res.json();

            if (Number(data.isBillSent) === 1) {
                alert("This invoice has already been submitted for approval and cannot be edited.");
                setIsInvoiceLocked(true);
                return;
            }

            setIsInvoiceLocked(false);
            setForm((prev) => ({
                ...prev,
                id: data.id,
                invoiceType: data.invoiceType || prev.invoiceType,
                creditCardNumber: data.creditCardNumber || prev.creditCardNumber
            }));

            fetchSavedDocuments(data.id);

            if (Array.isArray(data.creditItems) && data.creditItems.length > 0) {
                setBillRows(
                    data.creditItems.map((item) => {
                        const amt = Number(item.amount) || 0;
                        const toPct = (v) => (amt > 0 ? (Number(v) || 0) / amt * 100 : 0);

                        return {
                            natureOfExpense: item.expenseType || "",
                            amount: item.amount ?? "",
                            igst: toPct(item.igst),
                            cgst: toPct(item.cgst),
                            sgst: toPct(item.sgst),
                            total: item.total ?? 0,
                            documents: [],
                            hasSavedDoc: true
                        };
                    })
                );
            }
        } catch (error) {
            console.error("Check Existing Invoice Error:", error);
        }
    }, [fetchSavedDocuments]);

    

    const fetchApprovalHistory = useCallback(async (invoiceNo) => {
        if (!invoiceNo) {
            setApprovalHistory([]);
            return;
        }
        setLoadingHistory(true);
        try {
            const res = await fetch(`/api/invoice/invoice-approval-history-by-no/${invoiceNo}`);
            const data = await res.json();
            setApprovalHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Fetch Approval History Error:", error);
            setApprovalHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

   

    useEffect(() => {
        fetch("/api/PettyCashCredit/credit-cards")
            .then((res) => res.json())
            .then((data) => setCreditCards(Array.isArray(data) ? data : []))
            .catch((err) => {
                console.error("Fetch Credit Cards Error:", err);
                setCreditCards([]);
            });
    }, []);

    // Re-sync requestedBy/employeeId if the user context resolves after mount
    // (e.g. UserContext loads asynchronously on first render).
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            requestedBy: prev.requestedBy || currentUserName,
            employeeId: prev.employeeId || currentUserEmpId
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserName, currentUserEmpId]);

    const fetchRequests = useCallback(async () => {
        setLoadingList(true);

        try {
            const res = await fetch("/api/PettyCashCredit");
            const data = await res.json();

            console.log("PettyCashCredit API:", data);
            console.log("Is array:", Array.isArray(data));

            if (!res.ok) {
                console.error("API ERROR:", data);
                setRequests([]);
                return;
            }

            if (Array.isArray(data)) {
                setRequests(data);
            } else {
                console.error("API did NOT return an array:", data);
                setRequests([]);
            }
        } catch (error) {
            console.error("Fetch Requests Error:", error);
            setRequests([]);
        } finally {
            setLoadingList(false);
        }
    }, []);


    useEffect(() => {
        fetchRequests();
        const intervalId = setInterval(fetchRequests, 15000);
        return () => clearInterval(intervalId);
    }, [fetchRequests]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };
    const handleBillRowChange = (index, field, value) => {
        setBillErrors((prev) => {
            const key = `${index}_${field === "natureOfExpense" ? "nature" : field}`;
            if (!prev[key]) return prev;
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });

        setBillRows((prev) => {
            const updated = [...prev];

            updated[index] = {
                ...updated[index],
                [field]: value
            };

            if (["amount", "igst", "cgst", "sgst"].includes(field)) {
                const amount = Number(updated[index].amount) || 0;
                const igstPct = Number(updated[index].igst) || 0;
                const cgstPct = Number(updated[index].cgst) || 0;
                const sgstPct = Number(updated[index].sgst) || 0;

                const taxAmount = amount * (igstPct + cgstPct + sgstPct) / 100;

                updated[index].total = amount + taxAmount;
            }

            return updated;
        });
    };

    const handleBillDocumentChange = (index, files) => {
        setBillErrors((prev) => {
            const key = `${index}_document`;
            if (!prev[key]) return prev;
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });

        setBillRows((prev) => {
            const updated = [...prev];
            const newFiles = Array.from(files || []);

            updated[index] = {
                ...updated[index],
                documents: [...(updated[index].documents || []), ...newFiles]
            };

            return updated;
        });
    };

    const handleRemoveBillDocument = (rowIndex, fileIndex) => {
        setBillRows((prev) => {
            const updated = [...prev];
            updated[rowIndex] = {
                ...updated[rowIndex],
                documents: updated[rowIndex].documents.filter((_, i) => i !== fileIndex)
            };
            return updated;
        });
    };

    const handleAddBillRow = () => {
        const lastIndex = billRows.length - 1;
        const lastRow = billRows[lastIndex];
        const errs = {};

        if (!lastRow.natureOfExpense.trim()) {
            errs[`${lastIndex}_nature`] = "Description is required";
        }
        if (!lastRow.amount || Number(lastRow.amount) <= 0) {
            errs[`${lastIndex}_amount`] = "Amount must be greater than zero";
        }
        if ((!lastRow.documents || lastRow.documents.length === 0) && !lastRow.hasSavedDoc) {
            errs[`${lastIndex}_document`] = "Document is required";
        }
        if (Object.keys(errs).length > 0) {
            setBillErrors((prev) => ({ ...prev, ...errs }));
            return;
        }

        setBillErrors({});
        setBillRows((prev) => [...prev, emptyBillRow()]);
    };
    const handleDeleteBillRow = (index) => {
        setBillRows((prev) => {
            if (prev.length <= 1) {
                alert("At least one row is required.");
                return prev;
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const validateBillDetails = () => {
        const errors = {};

        const usedRows = billRows.filter(
            (row) =>
                row.natureOfExpense.trim() ||
                Number(row.amount) > 0 ||
                (row.documents && row.documents.length > 0)
        );

        if (usedRows.length === 0) {
            alert("Please enter at least one bill detail.");
            return false;
        }

        billRows.forEach((row, index) => {
            const used =
                row.natureOfExpense.trim() ||
                Number(row.amount) > 0 ||
                (row.documents && row.documents.length > 0);

            if (!used) return;

            if (!row.natureOfExpense.trim()) {
                errors[`${index}_nature`] =
                    "Description is required";
            }

            if (!row.amount || Number(row.amount) <= 0) {
                errors[`${index}_amount`] =
                    "Amount must be greater than zero";
            }

            if ((!row.documents || row.documents.length === 0) && !row.hasSavedDoc) {
                errors[`${index}_document`] = "Document is required";
            }
        });

        setBillErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleClearBillDetails = () => {
        setBillRows(
            Array.from({ length: 1 }, emptyBillRow)
        );

        setBillErrors({});
    };


    const validate = () => {
        const newErrors = {};
        if (!form.requestType) newErrors.requestType = "Type of Request is required";
        if (!form.employeeId.trim()) newErrors.employeeId = "Employee ID is required";
        if (!form.date) newErrors.date = "Date is required";
        if (!form.requestedBy.trim()) newErrors.requestedBy = "Requested by is required";

        if (form.requestType === "Petty Cash") {
            if (!form.projectDepartmentName.trim())
                newErrors.projectDepartmentName = "Project/Department Name is required";
            if (!form.deliveryManager) newErrors.deliveryManager = "Delivery Manager is required";
            if (!form.modeOfPayment) newErrors.modeOfPayment = "Mode of Payment is required";
            if (!form.paymentStatus) newErrors.paymentStatus = "Payment Status is required";
            if (!form.vendor.trim()) newErrors.vendor = "Vendor is required";
            if (!form.amount) newErrors.amount = "Amount is required";
            else if (Number(form.amount) <= 0) newErrors.amount = "Amount must be greater than zero";
        }

        if (form.requestType === "Credit") {

            if (!form.invoiceType) {
                newErrors.invoiceType = "Invoice Type is required";
            }

            if (!form.creditCardNumber) {
                newErrors.creditCardNumber =
                    "Credit Card Number is required";
            }

            if (!form.invoiceNumber.trim()) {
                newErrors.invoiceNumber =
                    "Invoice Number is required";
            }
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateRequest = async () => {
        if (!validate()) return;

        try {
            const response = await fetch("/api/PettyCashCredit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    requestType: form.requestType,
                    employeeId: form.employeeId,
                    date: form.date,
                    requestedBy: form.requestedBy,
                    ...(form.requestType === "Petty Cash" && {
                        project: form.projectDepartmentName,
                        deliveryManager: form.deliveryManager,
                        modeOfPayment: Number(form.modeOfPayment),
                        paymentStatus: Number(form.paymentStatus),
                        vendor: form.vendor,
                        amount: Number(form.amount)
                    }),
                    ...(form.requestType === "Credit" && {
                        creditCardNumber: form.creditCardNumber,
                        invoiceType: form.invoiceType,
                        invoiceNo: form.invoiceNumber  
                    })
                })
            });

            const data = await response.json();

            if (!response.ok) {
                alert(data.message || "Failed to create request");
                return;
            }

            setForm((prev) => ({ ...prev, id: data.id }));
            alert("Request created successfully");
            fetchRequests();
        } catch (error) {
            console.error("Create Request Error:", error);
            alert("Error creating request");
        }
    };

    const handleSendBillForApproval = async () => {
        let requestId = form.id;
        let currentRequest = selectedRequest;

        // Credit has no separate "Create Request" step in the UI —
        // create the request now, right before sending the bill.
        if (!requestId && form.requestType === "Credit") {
            if (!validate()) return;
            if (!validateBillDetails()) return;


            const creditItemsPayload = billRows
                .filter(row => row.natureOfExpense.trim() || Number(row.amount) > 0 ||
                    (row.documents && row.documents.length > 0))
                .map(row => ({
                    expenseType: row.natureOfExpense,
                    amount: Number(row.amount) || 0,
                    igst: (Number(row.amount) || 0) * (Number(row.igst) || 0) / 100,
                    cgst: (Number(row.amount) || 0) * (Number(row.cgst) || 0) / 100,
                    sgst: (Number(row.amount) || 0) * (Number(row.sgst) || 0) / 100,
                    totalValue: Number(row.total) || 0
                }));

            const totalAmount = creditItemsPayload.reduce(
                (sum, i) => sum + i.totalValue,
                0
            );


            try {
                const response = await fetch("/api/PettyCashCredit", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        requestType: form.requestType,
                        employeeId: form.employeeId,
                        date: form.date,
                        requestedBy: form.requestedBy,
                        creditCardNumber: form.creditCardNumber,
                        invoiceType: form.invoiceType, 
                        invoiceNo: form.invoiceNumber,
                        amount: totalAmount,
                        creditItems: creditItemsPayload
                    })
                });
                const data = await response.json();

                if (!response.ok) {
                    alert(data.message || "Failed to create request");
                    return;
                }

                requestId = data.id;
                setForm((prev) => ({ ...prev, id: requestId }));
                currentRequest = { id: requestId, isBillSent: 0 };
            } catch (error) {
                console.error("Auto-create Credit request error:", error);
                alert("Error creating request");
                return;
            }
        }

        if (!requestId) {
            alert("Please create the request first.");
            return;
        }

        if (!currentRequest) {
            alert("Request not found.");
            return;
        }

        if (form.requestType !== "Credit") {
            if (
                Number(currentRequest.isDMApproved) !== 1 ||
                Number(currentRequest.isFinanceApproved) !== 1
            ) {
                alert(
                    "Request must be approved by Delivery Manager and Finance first."
                );
                return;
            }
        }

        if (Number(currentRequest.isBillSent) === 1) {
            alert("Bill has already been sent.");
            return;
        }

        if (!validateBillDetails()) {
            return;
        }

        setSavingBill(true);

        try {
            const formData = new FormData();

            formData.append("requestId", String(requestId));

            billRows.forEach((row, index) => {
                const used =
                    row.natureOfExpense.trim() ||
                    Number(row.amount) > 0 ||
                    (row.documents && row.documents.length > 0);

                if (!used) return;

                formData.append(
                    `billDetails[${index}][natureOfExpense]`,
                    row.natureOfExpense
                );

                formData.append(
                    `billDetails[${index}][amount]`,
                    String(Number(row.amount) || 0)
                );

                const rowAmt = Number(row.amount) || 0;
                const isCredit = form.requestType === "Credit";

                formData.append(
                    `billDetails[${index}][igst]`,
                    String(isCredit ? (rowAmt * (Number(row.igst) || 0)) / 100 : Number(row.igst) || 0)
                );

                formData.append(
                    `billDetails[${index}][cgst]`,
                    String(isCredit ? (rowAmt * (Number(row.cgst) || 0)) / 100 : Number(row.cgst) || 0)
                );

                formData.append(
                    `billDetails[${index}][sgst]`,
                    String(isCredit ? (rowAmt * (Number(row.sgst) || 0)) / 100 : Number(row.sgst) || 0)
                );

                formData.append(
                    `billDetails[${index}][total]`,
                    String(Number(row.total) || 0)
                );

                if (row.documents && row.documents.length > 0) {
                    row.documents.forEach((file, fileIndex) => {
                        formData.append(
                            `billDetails[${index}][documents][${fileIndex}]`,
                            file
                        );
                    });
                }
            });

            const response = await fetch(
                `/api/PettyCashCredit/${requestId}/send-for-approval`,
                {
                    method: "POST",
                    body: formData
                }
            );

            const data = await response.json();

            if (!response.ok) {
                alert(
                    data.message ||
                    "Failed to send bill for approval"
                );
                return;
            }

            alert("Bill sent for approval successfully.");

            handleClearBillDetails();

            await fetchRequests();

        } catch (error) {
            console.error(
                "Send Bill For Approval Error:",
                error
            );

            alert("Error sending bill for approval.");
        } finally {
            setSavingBill(false);
        }
    };
    const handleSaveAsDraft = async () => {
        if (!form.invoiceType || !form.creditCardNumber || !form.invoiceNumber.trim()) {
            alert("Invoice Type, Credit Card Number, and Invoice Number are required to save a draft.");
            return;
        }

        setSavingBill(true);
        try {
            const formData = new FormData();
            formData.append("id", form.id || "");
            formData.append("requestType", "Credit");
            formData.append("employeeId", form.employeeId);
            formData.append("date", form.date);
            formData.append("requestedBy", form.requestedBy);
            formData.append("creditCardNumber", form.creditCardNumber);
            formData.append("invoiceType", form.invoiceType);
            formData.append("invoiceNo", form.invoiceNumber);

            billRows.forEach((row, index) => {
                const used = row.natureOfExpense.trim() || Number(row.amount) > 0 ||
                    (row.documents && row.documents.length > 0);
                if (!used) return;

                const rowAmt = Number(row.amount) || 0;

                formData.append(`billDetails[${index}][natureOfExpense]`, row.natureOfExpense);
                formData.append(`billDetails[${index}][amount]`, String(rowAmt));
                formData.append(`billDetails[${index}][igst]`, String((rowAmt * (Number(row.igst) || 0)) / 100));
                formData.append(`billDetails[${index}][cgst]`, String((rowAmt * (Number(row.cgst) || 0)) / 100));
                formData.append(`billDetails[${index}][sgst]`, String((rowAmt * (Number(row.sgst) || 0)) / 100));
                formData.append(`billDetails[${index}][total]`, String(Number(row.total) || 0));

                if (row.documents && row.documents.length > 0) {
                    row.documents.forEach((file, fileIndex) => {
                        formData.append(`billDetails[${index}][documents][${fileIndex}]`, file);
                    });
                }
            });

            const response = await fetch("/api/PettyCashCredit/save-draft", {
                method: "POST",
                body: formData
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.message || "Failed to save draft");
                return;
            }

            setForm((prev) => ({ ...prev, id: data.id }));
            await fetchSavedDocuments(data.id);
            alert("Draft saved.");
            fetchRequests();
        } catch (error) {
            console.error("Save Draft Error:", error);
            alert("Error saving draft");
        } finally {
            setSavingBill(false);
        }
    };

    const handleClear = () => {
        setForm(buildInitialForm());
        setErrors({});
    };
    const handleRequestIdClick = (row) => {
        setForm((prev) => ({
            ...prev,
            id: row.id,
            requestType: row.typeOfRequest === "Petty Cash"
                ? "Petty Cash"
                : "Credit",
            employeeId: row.employeeId || prev.employeeId,
            date: row.reqDate || prev.date,
            requestedBy: row.requestedBy || prev.requestedBy,
            projectDepartmentName: row.project || "",
            deliveryManager: row.deliveryManager || "",
            modeOfPayment: row.modeOfPayment
                ? String(row.modeOfPayment)
                : "",
            paymentStatus: row.paymentStatus
                ? String(row.paymentStatus)
                : "",
            vendor: row.vendor || "",
            amount: row.amount || "",
            creditCardNumber: row.creditCardNumber || "",
            invoiceType: row.invoiceType || "", 
            invoiceNumber: row.invoiceNumber || ""
        }));

        setViewMode("billDetails");
        fetchSavedDocuments(row.id); 
        fetchApprovalHistory(row.invoiceNumber || row.invoiceNo);
    };

    const handleBackToRequest = () => {
        setViewMode("request");
    };

    const selectedRequest = Array.isArray(requests)
        ? requests.find(
            (r) => Number(r.id) === Number(form.id)
        )
        : null;




    // Bill can only be sent once the request itself is fully approved,
    // and only once (not already sent).
    const canSendBill = (() => {
        if (savingBill) {
            return false;
        }

        // CREDIT
        if (form.requestType === "Credit") {
            // New Credit request: allow user to create request + send bill
            if (!form.id) {
                return true;
            }

            // Existing Credit request:
            // do not allow sending again if bill is already sent
            if (selectedRequest) {
                return Number(selectedRequest.isBillSent) !== 1;
            }

            return false;
        }

        // PETTY CASH
        return (
            !!form.id &&
            !!selectedRequest &&
            Number(selectedRequest.isBillSent) !== 1 &&
            Number(selectedRequest.isDMApproved) === 1 &&
            Number(selectedRequest.isFinanceApproved) === 1
        );
    })();



    // Only let a user click into their own submitted requests.
    const myRequests = Array.isArray(requests)
        ? requests.filter(
            (row) =>
                row.requestedBy === currentUserName ||
                (currentUserEmpId && row.employeeId === currentUserEmpId)
        )
        : [];


    return (
        <DashboardLayout>
            <style>{noSpinnerStyle}</style>
            <div className="container-fluid py-2">

                {/* HEADER BANNER */}
                <div
                    style={{
                        background: "linear-gradient(135deg, #1a4b8c, #0d6efd)",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        marginBottom: "8px",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}
                >
                    <div
                        style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px"
                        }}
                    >
                        💳
                    </div>
                    <div>
                        <h4 className="mb-0 fw-bold">
                            {form.requestType === "Credit" ? "Credit Card" : "Petty Cash"}
                        </h4>
                        <div style={{ fontSize: "13px", opacity: 0.85 }}>
                            {form.requestType === "Credit"
                                ? "Create and submit a new credit card request"
                                : "Create and submit a new petty cash request"}
                        </div>
                    </div>
                </div>

                {/* REQUEST FORM */}
                <div
                    style={{
                        backgroundColor: isDark ? "#0f1b2e" : "#fff",
                        border: isDark ? "1px solid #24344a" : "1px solid #d9d9d9",
                        borderRadius: "2px",
                        padding: "0 12px 12px"
                    }}
                >
                    {/* REQUEST / BILL TABS */}
                    <div
                        style={{
                            height: "34px",
                            borderBottom: "1px solid #d9d9d9",
                            marginBottom: "10px",
                            display: "flex"
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleBackToRequest}
                            style={{
                                height: "34px",
                                padding: "0 14px",
                                background: "transparent",
                                border: "none",
                                borderBottom:
                                    viewMode === "request"
                                        ? "3px solid #198754"
                                        : "3px solid transparent",
                                color: viewMode === "request" ? "#198754" : "#6c757d",
                                fontWeight: "600",
                                fontSize: "14px"
                            }}
                        >
                            Request
                        </button>

                        {viewMode === "billDetails" && (
                            <button
                                type="button"
                                style={{
                                    height: "34 px",
                                    padding: "0 14px",
                                    background: "transparent",
                                    border: "none",
                                    borderBottom: "3px solid #198754",
                                    color: "#198754",
                                    fontWeight: "600",
                                    fontSize: "14px"
                                }}
                            >
                                Bill
                            </button>
                        )}
                    </div>

                    {viewMode === "request" && (
                        <div>

                           

                                    {/* STEP 1 — REQUEST DETAILS */}
                            <div
                                style={{
                                    backgroundColor: isDark ? "#0f1b2e" : "#fff",
                                    border: isDark ? "1px solid #24344a" : "1px solid #d9d9d9",
                                    borderRadius: "8px",
                                    marginBottom: "10px",
                                    overflow: "hidden"
                                }}
                            >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: "8px 16px",
                                                borderBottom: "1px solid #e9ecef"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "22px",
                                                    height: "22px",
                                                    borderRadius: "50%",
                                                    background: "#0d6efd",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 700,
                                                    fontSize: "14px",
                                                    flexShrink: 0
                                                }}
                                            >
                                                1
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: "13px" }}>Request Details</div>
                                                <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                                    Enter the basic information for this request.
                                                </div>
                                            </div>
                                        </div>

                                <div style={{ padding: "8px 16px" }}>
                                            <div className="row">

                                                {/* TYPE OF REQUEST */}
                                                <div className="col-md-3 mb-2">
                                            <label className="form-label fw-semibold mb-0" style={{ fontSize: "12px" }}>
                                                        Type of Request <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="d-flex align-items-center gap-3" style={{ height: "38px" }}>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="requestType"
                                                                checked={form.requestType === "Petty Cash"}
                                                                onChange={() => handleChange("requestType", "Petty Cash")}
                                                            />
                                                            <label className="form-check-label">Petty Cash</label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="requestType"
                                                                checked={form.requestType === "Credit"}
                                                                onChange={() => handleChange("requestType", "Credit")}
                                                            />
                                                            <label className="form-check-label">Credit</label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* DATE */}
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label fw-semibold">
                                                        Date <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={form.date}
                                                        onChange={(e) => handleChange("date", e.target.value)}
                                                    />
                                                </div>

                                                {/* EMPLOYEE ID */}
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label fw-semibold">
                                                        Employee ID <span className="text-danger">*</span>
                                                    </label>
                                                    <input type="text" className="form-control" value={form.employeeId} readOnly />
                                                </div>

                                                {/* REQUESTED BY */}
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label fw-semibold">
                                                        Requested by <span className="text-danger">*</span>
                                                    </label>
                                                    <input type="text" className="form-control" value={form.requestedBy} readOnly />
                                                </div>

                                                {form.requestType === "Credit" && (
                                                    <>
                                                        {/* INVOICE TYPE */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Invoice Type <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="form-select"
                                                                value={form.invoiceType}
                                                                onChange={(e) => handleChange("invoiceType", e.target.value)}
                                                            >
                                                                <option value="0">Select</option>
                                                                <option value="Tax Invoice">Tax Invoice</option>
                                                                <option value="Debit Note">Debit Note</option>
                                                                <option value="Credit Note">Credit Note</option>
                                                                <option value="Proforma Invoice">Proforma Invoice</option>
                                                            </select>
                                                            {errors.invoiceType && (
                                                                <small className="text-danger d-block">{errors.invoiceType}</small>
                                                            )}
                                                        </div>

                                                {/* CREDIT CARD NUMBER */}
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label fw-semibold">
                                                        Credit Card Number <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select"
                                                        value={form.creditCardNumber}
                                                        onChange={(e) => handleChange("creditCardNumber", e.target.value)}
                                                    >
                                                        <option value="">-- Select --</option>
                                                        {creditCards.map((card) => (
                                                            <option key={card.id} value={card.cardNumber}>
                                                                {card.cardNumber}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.creditCardNumber && (
                                                        <small className="text-danger d-block">{errors.creditCardNumber}</small>
                                                    )}
                                                </div>

                                                        {/* INVOICE NUMBER */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Invoice Number <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={form.invoiceNumber}
                                                                onChange={(e) => handleChange("invoiceNumber", e.target.value)}
                                                                onBlur={(e) => checkExistingInvoice(e.target.value)}
                                                                placeholder="Enter Invoice Number"
                                                    />
                                                    {errors.invoiceNumber && (
                                                        <small className="text-danger d-block">{errors.invoiceNumber}</small>
                                                    )}
                                                        </div>
                                                    </>
                                                )}

                                                {form.requestType === "Petty Cash" && (
                                                    <>
                                                        {/* PROJECT / DEPARTMENT */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Project/Department Name <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={form.projectDepartmentName}
                                                                onChange={(e) => handleChange("projectDepartmentName", e.target.value)}
                                                                placeholder="Enter Project/Department Name"
                                                            />
                                                        </div>

                                                        {/* DELIVERY MANAGER */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Delivery Manager <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="form-select"
                                                                value={form.deliveryManager}
                                                                onChange={(e) => handleChange("deliveryManager", e.target.value)}
                                                            >
                                                                <option value="">Select Delivery Manager</option>
                                                                <option value="Santosh Nandiyath">Santosh Nandiyath</option>
                                                                <option value="Sandeep Roychoudhury">Sandeep Roychoudhury</option>
                                                                <option value="Other">Other</option>
                                                            </select>
                                                        </div>

                                                        {/* MODE OF PAYMENT */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Mode of Payment <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="form-select"
                                                                value={form.modeOfPayment}
                                                                onChange={(e) => handleChange("modeOfPayment", e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="1">Cash</option>
                                                                <option value="2">Bank Transfer</option>
                                                                <option value="3">UPI</option>
                                                            </select>
                                                        </div>

                                                        {/* PAYMENT STATUS */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Payment Status <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="form-select"
                                                                value={form.paymentStatus}
                                                                onChange={(e) => handleChange("paymentStatus", e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="1">Non-Billable</option>
                                                                <option value="2">Billable</option>
                                                                <option value="3">Rejected</option>
                                                            </select>
                                                        </div>

                                                        {/* VENDOR */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Vendor <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={form.vendor}
                                                                onChange={(e) => handleChange("vendor", e.target.value)}
                                                                placeholder="Enter Vendor"
                                                            />
                                                        </div>

                                                        {/* AMOUNT */}
                                                        <div className="col-md-3 mb-2">
                                                            <label className="form-label fw-semibold">
                                                                Amount <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                className="form-control"
                                                                value={form.amount}
                                                                onChange={(e) => handleChange("amount", e.target.value)}
                                                                placeholder="Enter Amount"
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                            </div>
                                        </div>
                                    </div>

                            {/* BUTTONS */}
                            {form.requestType === "Petty Cash" && (
                            <div
                                className="d-flex justify-content-center"
                                style={{
                                    gap: "8px",
                                    marginTop: "8 px"
                                }}
                            >
                                <button
                                    type="button"
                                    className="btn text-white"
                                    style={{
                                        backgroundColor: "#0aa6d6",
                                        minWidth: "150px",
                                        height: "32px",
                                        fontWeight: "600"
                                    }}
                                    onClick={handleCreateRequest}
                                >
                                    Create Request
                                </button>

                                <button
                                    type="button"
                                    className="btn text-white"
                                    style={{
                                        backgroundColor: canSendBill
                                            ? "#0aa6d6"
                                            : "#5bc0de",
                                        minWidth: "210px",
                                        height: "32px",
                                        fontWeight: "600"
                                    }}
                                    onClick={handleSendBillForApproval}
                                        disabled={!canSendBill || savingBill}

                                >
                                    Send Bill For Approval
                                </button>

                                <button
                                    type="button"
                                    className="btn text-white"
                                    style={{
                                        backgroundColor: "#0aa6d6",
                                        minWidth: "100px",
                                        height: "32px",
                                        fontWeight: "600"
                                    }}
                                    onClick={handleClear}
                                >
                                    Clear
                                </button>
                            </div>
                            )}
                        </div>
                    )}
                            {/* SELECTED REQUEST — BILL DETAILS, shown once bill is fully approved */}
                            {selectedRequest &&
                                Number(selectedRequest.isBillFinanceApproved) === 1 && (

                                    <div
                                        className="mt-4 p-3 rounded"
                                        style={{ backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7" }}
                                    >
                                        <div className="fw-bold mb-2" style={{ color: "#198754" }}>
                                            ✓ Bill Approved
                                        </div>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <strong>Vendor:</strong> {selectedRequest.vendor}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Mode of Payment:</strong>{" "}
                                                {MODE_OF_PAYMENT[selectedRequest.modeOfPayment] || selectedRequest.modeOfPayment}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Amount:</strong>{" "}
                                                ₹{Number(selectedRequest.amount).toLocaleString("en-IN")}
                                            </div>
                                        </div>
                                    </div>
                                )}
                    
                    {/* ============================================================

    BILL DETAILS

    Copied from your Bill Details implementation.

    This appears when Request ID is clicked.

============================================================ */}

                    {viewMode === "billDetails" && selectedRequest && (

                        <div

                            className="card-body"

                            style={{

                                padding: "20px 25px 22px"

                            }}
                        >

                            {/* ======================================================

            EXPENSE DETAILS TABLE

        ====================================================== */}

                            <div
                                style={{
                                    marginTop: "25px",
                                    borderTop: "1px solid #d5dde3",
                                    paddingTop: "15px",
                                    width: "100%",
                                    overflow: "hidden"
                                }}
                            >
                                <div style={{ maxHeight: "300px", overflowY: "auto" }}>

                                    <table

                                        className="table table-bordered mb-0"

                                        style={{

                                            minWidth: "100%",

                                            fontSize: "13px",

                                            tableLayout: "fixed"

                                        }}
                                    >

                                    <thead>

                                        <tr

                                            style={{

                                                backgroundColor: "#18212b",

                                                color: "#ffffff"

                                            }}
                                        >

                                            {/* SL NO */}
                                            <th

                                                style={{

                                                    width: "5%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700,

                                                    whiteSpace: "nowrap"

                                                }}
                                            >

                                                SL. NO
                                            </th>

                                            {/* NATURE */}
                                            <th

                                                style={{

                                                    width: "23%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                DESCRIPTION*
                                            </th>

                                            {/* AMOUNT */}
                                            <th

                                                style={{

                                                    width: "11%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                AMOUNT*
                                            </th>

                                            {/* IGST */}
                                            <th

                                                style={{

                                                    width: "10%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                IGST
                                            </th>

                                            {/* CGST */}
                                            <th

                                                style={{

                                                    width: "10%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                CGST
                                            </th>

                                            {/* SGST */}
                                            <th

                                                style={{

                                                    width: "10%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                SGST
                                            </th>

                                            {/* TOTAL */}
                                            <th

                                                style={{

                                                    width: "11%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                TOTAL*
                                            </th>

                                            {/* DOCUMENT */}
                                            <th

                                                style={{

                                                    width: "20%",

                                                    textAlign: "center",

                                                    padding: "8px 5px",

                                                    fontWeight: 700

                                                }}
                                            >

                                                DOCUMENT*
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody>
                                        {billRows.map((row, index) => (
                                            <tr key={index}>

                                                {/* SL NO */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        verticalAlign: "middle",
                                                        padding: "4px"
                                                    }}
                                                >
                                                    {index + 1}
                                                </td>

                                                {/* NATURE */}
                                                <td style={{ padding: "4px" }}>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        style={{ height: "30px" }}
                                                        value={row.natureOfExpense}
                                                        onChange={(e) => handleBillRowChange(index, "natureOfExpense", e.target.value)}
                                                        placeholder="Description"
                                                    />
                                                    {billErrors[`${index}_nature`] && (
                                                        <small className="text-danger d-block">{billErrors[`${index}_nature`]}</small>
                                                    )}
                                                </td>

                                                {/* AMOUNT */}
                                                <td style={{ padding: "4px" }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="form-control form-control-sm"
                                                        style={{ height: "30px" }}
                                                        value={row.amount}
                                                        onChange={(e) => handleBillRowChange(index, "amount", e.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                    {billErrors[`${index}_amount`] && (
                                                        <small className="text-danger d-block">{billErrors[`${index}_amount`]}</small>
                                                    )}
                                                </td>

                                                {/* IGST */}
                                                <td style={{ padding: "4px" }}>
                                                    <div style={{ position: "relative", width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            className="form-control form-control-sm no-spinner"
                                                            value={row.igst}
                                                            onChange={(e) =>
                                                                handleBillRowChange(
                                                                    index,
                                                                    "igst",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder="0.00"
                                                            style={{
                                                                height: "30px",
                                                                width: "100%",
                                                                paddingRight: "25px",
                                                                textAlign: "right"
                                                            }}
                                                        />

                                                        <span
                                                            style={{
                                                                position: "absolute",
                                                                right: "8px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                color: "#6c757d",
                                                                fontWeight: "600",
                                                                pointerEvents: "none"
                                                            }}
                                                        >
                                                            %
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* CGST */}
                                                <td style={{ padding: "4px" }}>
                                                    <div style={{ position: "relative", width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            className="form-control form-control-sm no-spinner"
                                                            value={row.cgst}
                                                            onChange={(e) =>
                                                                handleBillRowChange(
                                                                    index,
                                                                    "cgst",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder="0.00"
                                                            style={{
                                                                height: "30px",
                                                                width: "100%",
                                                                paddingRight: "25px",
                                                                textAlign: "right"
                                                            }}
                                                        />

                                                        <span
                                                            style={{
                                                                position: "absolute",
                                                                right: "8px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                color: "#6c757d",
                                                                fontWeight: "600",
                                                                pointerEvents: "none"
                                                            }}
                                                        >
                                                            %
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* SGST */}
                                                <td style={{ padding: "4px" }}>
                                                    <div style={{ position: "relative", width: "100%" }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            className="form-control form-control-sm no-spinner"
                                                            value={row.sgst}
                                                            onChange={(e) =>
                                                                handleBillRowChange(
                                                                    index,
                                                                    "sgst",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder="0.00"
                                                            style={{
                                                                height: "30px",
                                                                width: "100%",
                                                                paddingRight: "25px",
                                                                textAlign: "right"
                                                            }}
                                                        />

                                                        <span
                                                            style={{
                                                                position: "absolute",
                                                                right: "8px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                color: "#6c757d",
                                                                fontWeight: "600",
                                                                pointerEvents: "none"
                                                            }}
                                                        >
                                                            %
                                                        </span>
                                                    </div>
                                                </td>


                                                {/* TOTAL */}
                                                <td style={{ padding: "4px" }}>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        style={{
                                                            height: "30px",
                                                            backgroundColor: "#f8f9fa",
                                                            fontWeight: 600
                                                        }}
                                                        value={row.total}
                                                        readOnly
                                                    />
                                                </td>

                                                {/* DOCUMENT */}
                                                <td style={{ padding: "4px" }}>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        className="form-control form-control-sm"
                                                        style={{ height: "30px", fontSize: "12px" }}
                                                        onChange={(e) => {
                                                            handleBillDocumentChange(index, e.target.files);
                                                            e.target.value = "";
                                                        }}
                                                    />
                                                    <small className="text-muted d-block" style={{ fontSize: "10px" }}>
                                                        Any file type | Multiple files allowed
                                                    </small>
                                                    {savedDocs.filter((d) => d.rowIndex === index).length > 0 && (
                                                        <div className="mt-1">
                                                            {savedDocs
                                                                .filter((d) => d.rowIndex === index)
                                                                .map((d, di) => (
                                                                    <div key={di}>
                                                                    <a
                                                                        href={d.filePath}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{ fontSize: "11px" }}
                    >
                                                                        {d.originalFileName || d.filePath.split("/").pop()}
                                                                    </a>
                </div>
                                                    ))}
                                                </div>
)}
                                                    {row.documents && row.documents.length > 0 && (
                                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                                            {row.documents.map((f, fi) => (
                                                                <span
                                                                    key={fi}
                                                                    className="badge bg-light text-dark border d-flex align-items-center"
                                                                    style={{ fontSize: "10px", gap: "4px" }}
                                                                >
                                                                    {f.name}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveBillDocument(index, fi)}
                                                                        style={{ border: "none", background: "none", color: "#dc3545", fontWeight: "bold", cursor: "pointer", padding: 0, lineHeight: 1 }}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {billErrors[`${index}_document`] && (
                                                        <small className="text-danger d-block">{billErrors[`${index}_document`]}</small>
                                                    )}
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>


                                </table>

                                </div>
                            </div>


                            {/* ======================================================

            BILL DETAILS BUTTONS

        ====================================================== */}

                            <div

                                className="d-flex justify-content-center"

                                style={{

                                    gap: "8px",

                                    marginTop: "10px",

                                    paddingTop: "8px"

                                }}
                            >

                                {/* SEND BILL */}
                                <button

                                    type="button"

                                    className="btn px-4 fw-semibold text-white"

                                    style={{

                                        backgroundColor: canSendBill

                                            ? "#0d6efd"

                                            : "#9ec5fe"

                                    }}

                                    onClick={handleSendBillForApproval}

                                    disabled={!canSendBill}

                                    title={
                                        form.requestType === "Credit"
                                            ? (canSendBill ? "" : "Bill has already been sent")
                                            : (!form.id
                                                ? "Create a request first"
                                                : !canSendBill
                                                    ? "Request must be fully approved by Delivery Manager and Finance first"
                                                    : "")
                                    }
                                >

                                    {savingBill ? "Sending..." : "Send Bill For Approval"}

                                </button>

                                {/* CLEAR */}
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-4 fw-semibold"
                                    onClick={handleClearBillDetails}
                                >
                                    Clear
                                </button>


                            </div>


                            {/* ======================================================

            BACK TO REQUEST

        ====================================================== */}

                            <div className="text-center mt-3">

                                <button

                                    type="button"

                                    className="btn btn-outline-secondary px-4"

                                    onClick={handleBackToRequest}
                                >

                                    Back to Request
                                </button>

                            </div>

                        </div>

                    )}

                </div>

                {form.requestType === "Credit" && viewMode === "request" ? (

                    /* CREDIT — bill/expense table with dynamic rows, replaces the request list */
                    <div className="card shadow-sm border-0">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "16px 20px",
                                borderBottom: "1px solid #e9ecef"
                            }}
                        >
                            <div
                                style={{
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "50%",
                                    background: "#0d6efd",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    flexShrink: 0
                                }}
                            >
                                2
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: "13px" }}>Goods / Services Details</div>
                            </div>
                        </div>

                        <div className="card-body" style={{ padding: "10px 16px 12px" }}>
                            <div className="goods-scroll" style={{ maxHeight: "620px", overflowY: "auto" }}>
                                <table
                                    className="table table-bordered mb-0"
                                    style={{ minWidth: "100%", fontSize: "13px", tableLayout: "fixed" }}
                                >
                                <thead>
                                    <tr style={{ backgroundColor: "#18212b", color: "#ffffff" }}>
                                        <th style={{ width: "5%", textAlign: "center", padding: "8px 5px", fontWeight: 700, whiteSpace: "nowrap" }}>SL. NO</th>
                                        <th style={{ width: "19%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>DESCRIPTION*</th>
                                        <th style={{ width: "9%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>AMOUNT*</th>
                                        <th style={{ width: "9%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>IGST (%)</th>
                                        <th style={{ width: "9%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>CGST (%)</th>
                                        <th style={{ width: "9%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>SGST (%)</th>
                                        <th style={{ width: "10%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>TOTAL*</th>
                                        <th style={{ width: "16%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>DOCUMENT*</th>
                                        <th style={{ width: "14%", textAlign: "center", padding: "8px 5px", fontWeight: 700 }}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billRows.map((row, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: "center", verticalAlign: "middle", padding: "4px" }}>{index + 1}</td>
                                            <td style={{ padding: "4px" }}>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm "
                                                    style={{ height: "30px" }}
                                                    value={row.natureOfExpense}
                                                    onChange={(e) => handleBillRowChange(index, "natureOfExpense", e.target.value)}
                                                    placeholder="Description"
                                                    disabled={isInvoiceLocked}
                                                />
                                                {billErrors[`${index}_nature`] && (
                                                    <small className="text-danger d-block">{billErrors[`${index}_nature`]}</small>
                                                )}
                                            </td>
                                            <td style={{ padding: "4px" }}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="form-control form-control-sm no-spinner"
                                                    style={{ height: "30px", textAlign: "right" }}
                                                    value={row.amount}
                                                    onChange={(e) => handleBillRowChange(index, "amount", e.target.value)}
                                                    placeholder="0.00"
                                                />
                                                {billErrors[`${index}_amount`] && (
                                                    <small className="text-danger d-block">{billErrors[`${index}_amount`]}</small>
                                                )}
                                            </td>
                                            {/* IGST */}
                                            <td style={{ padding: "4px" }}>
                                                <div style={{ position: "relative", width: "100%" }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        className="form-control form-control-sm  no-spinner"
                                                        value={row.igst}
                                                        onChange={(e) =>
                                                            handleBillRowChange(
                                                                index,
                                                                "igst",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="0.00"
                                                        style={{
                                                            height: "30px",
                                                            width: "100%",
                                                            paddingRight: "25px",
                                                            textAlign: "right"
                                                        }}
                                                        disabled={isInvoiceLocked}
                                                    />

                                                    <span
                                                        style={{
                                                            position: "absolute",
                                                            right: "8px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            color: "#6c757d",
                                                            fontWeight: "600",
                                                            pointerEvents: "none"
                                                        }}
                                                    >
                                                        %
                                                    </span>
                                                </div>
                                            </td>

                                            {/* CGST */}
                                            <td style={{ padding: "4px" }}>
                                                <div style={{ position: "relative", width: "100%" }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        className="form-control form-control-sm  no-spinner"
                                                        value={row.cgst}
                                                        onChange={(e) =>
                                                            handleBillRowChange(
                                                                index,
                                                                "cgst",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="0.00"
                                                        style={{
                                                            height: "30px",
                                                            width: "100%",
                                                            paddingRight: "25px",
                                                            textAlign: "right"
                                                        }}
                                                        disabled={isInvoiceLocked}
                                                    />

                                                    <span
                                                        style={{
                                                            position: "absolute",
                                                            right: "8px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            color: "#6c757d",
                                                            fontWeight: "600",
                                                            pointerEvents: "none"
                                                        }}
                                                    >
                                                        %
                                                    </span>
                                                </div>
                                            </td>

                                            {/* SGST */}
                                            <td style={{ padding: "4px" }}>
                                                <div style={{ position: "relative", width: "100%" }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        className="form-control form-control-sm  no-spinner"
                                                        value={row.sgst}
                                                        onChange={(e) =>
                                                            handleBillRowChange(
                                                                index,
                                                                "sgst",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="0.00"
                                                        style={{
                                                            height: "30px",
                                                            width: "100%",
                                                            paddingRight: "25px",
                                                            textAlign: "right"
                                                        }}
                                                        disabled={isInvoiceLocked}
                                                    />

                                                    <span
                                                        style={{
                                                            position: "absolute",
                                                            right: "8px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            color: "#6c757d",
                                                            fontWeight: "600",
                                                            pointerEvents: "none"
                                                        }}
                                                    >
                                                        %
                                                    </span>
                                                </div>
                                            </td>


                                            <td style={{ padding: "4px" }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    style={{ height: "30px", backgroundColor: "#f8f9fa", fontWeight: 600, textAlign: "right" }}
                                                    value={row.total}
                                                    readOnly
                                                />
                                            </td>
                                            <td style={{ padding: "4px" }}>
                                                <input
                                                    type="file"
                                                    multiple
                                                    className="form-control form-control-sm"
                                                    style={{ height: "30px", fontSize: "12px" }}
                                                    onChange={(e) => {
                                                        handleBillDocumentChange(index, e.target.files);
                                                        e.target.value = "";
                                                    }}
                                                    disabled={isInvoiceLocked}
                                                />
                                                <small className="text-muted d-block" style={{ fontSize: "10px" }}>
                                                    Any file type | Multiple files allowed
                                                </small>
                                                {savedDocs.filter((d) => d.rowIndex === index).length > 0 && (
    <div className="mt-1">
        {savedDocs
            .filter((d) => d.rowIndex === index)
            .map((d, di) => (
                <div key={di}>
                    <a
                        href={d.filePath}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: "11px" }}
                    >
                        {d.originalFileName || d.filePath.split("/").pop()}
                    </a>
                </div>
            ))}
    </div>
)}
                                                {row.documents && row.documents.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {row.documents.map((f, fi) => (
                                                            <span
                                                                key={fi}
                                                                className="badge bg-light text-dark border d-flex align-items-center"
                                                                style={{ fontSize: "10px", gap: "4px" }}
                                                            >
                                                                {f.name}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveBillDocument(index, fi)}
                                                                    style={{ border: "none", background: "none", color: "#dc3545", fontWeight: "bold", cursor: "pointer", padding: 0, lineHeight: 1 }}
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {billErrors[`${index}_document`] && (
                                                    <small className="text-danger d-block">{billErrors[`${index}_document`]}</small>
                                                )}
                                            </td>

                                            <td style={{ padding: "4px", textAlign: "center", overflow: "hidden" }}>
                                                <div className="d-flex flex-column align-items-center gap-1" style={{ width: "100%" }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        style={{
                                                            fontSize: "10px",
                                                            padding: "2px 4px",
                                                            lineHeight: "1.4",
                                                            width: "100%",
                                                            maxWidth: "100%",
                                                            minWidth: 0,
                                                            boxSizing: "border-box",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden"
                                                        }}
                                                        onClick={handleAddBillRow}
                                                        disabled={isInvoiceLocked}
                                                    >
                                                        + Add
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        style={{
                                                            fontSize: "10px",
                                                            padding: "2px 4px",
                                                            lineHeight: "1.4",
                                                            width: "100%",
                                                            maxWidth: "100%",
                                                            minWidth: 0,
                                                            boxSizing: "border-box",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden"
                                                        }}
                                                        onClick={() => handleDeleteBillRow(index)}
                                                        disabled={isInvoiceLocked}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-center" style={{ marginTop: "18px", gap: "8px" }}>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary px-4 fw-semibold"
                                    onClick={handleSaveAsDraft}
                                    disabled={savingBill || isInvoiceLocked}
                                >
                                    Save as Draft
                                </button>

                                <button
                                    type="button"
                                    className="btn px-4 fw-semibold text-white"
                                    style={{ backgroundColor: canSendBill ? "#0d6efd" : "#9ec5fe" }}
                                    onClick={handleSendBillForApproval}
                                    disabled={!canSendBill || isInvoiceLocked}
                                    title={
                                        form.requestType === "Credit"
                                            ? ""
                                            : !form.id
                                                ? "Create a request first"
                                                : !canSendBill
                                                    ? "Request must be fully approved by Delivery Manager and Finance first"
                                                    : ""
                                    }

                                >
                                    Send Bill For Approval

                                </button>
                            </div>
                        </div>
                    </div>
                ) : form.requestType === "Petty Cash" && viewMode === "request" ? (
                    /* MY SUBMITTED REQUESTS — only rows belonging to the logged-in user (Petty Cash only) */
                    <div className="card shadow-sm border-0 ">
                            <div className="card-body p-0">
                                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                    <table
                                        className="table table-bordered mb-0 align-middle stable-table"
                                        style={{ fontSize: "0.9rem", tableLayout: "fixed" }}
                                    >
                            <thead>
                                            <tr style={{ backgroundColor: isDark ? "#132238" : "#eaf6ef", color: isDark ? "#cfe3da" : "#14532d" }}>
                                    <th>Request ID</th>
                                    <th>Type</th>
                                    <th>Project/Dept</th>
                                    <th>Mode of Payment</th>
                                    <th>Date</th>
                                    <th>Payment Status</th>
                                    <th className="text-end">Amount</th>
                                    <th>Req Status</th>
                                    <th>Bill Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingList && (
                                    <tr><td colSpan={9} className="text-center py-3">Loading…</td></tr>
                                )}
                                {!loadingList && myRequests.length === 0 && (
                                    <tr><td colSpan={9} className="text-center py-3">No requests yet.</td></tr>
                                )}
                                {!loadingList && myRequests.map((row) => (
                                    <tr
                                        key={row.id}
                                        onClick={() => handleRequestIdClick(row)}
                                        style={{ cursor: "pointer", transform: "none", transition: "none" }}
                                    >
                                        <td style={{ color: "#b8860b", fontWeight: 600 }}>
                                            {row.reqId}
                                            {reqIdSuffixFor(row)}
                                        </td>
                                        <td>{row.typeOfRequest}</td>
                                        <td>{row.project}</td>
                                        <td>{MODE_OF_PAYMENT[row.modeOfPayment] || row.modeOfPayment}</td>
                                        <td>{row.reqDate}</td>
                                        <td>{PAYMENT_STATUS[row.paymentStatus] || row.paymentStatus}</td>
                                        <td className="text-end">{row.amount}</td>
                                        <td style={{ color: statusColor(reqStatusFor(row)), fontWeight: 600 }}>
                                            {reqStatusFor(row)}
                                        </td>
                                        <td style={{ color: statusColor(billStatusFor(row)), fontWeight: 600 }}>
                                            {billStatusFor(row)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            </table>
                        </div>
                            </div>
                    </div>
                ) : null}

            </div>
        </DashboardLayout>
    );
};

export default PettyCashCredit;